import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  alpha,
  Paper,
  InputAdornment,
  Divider,
  Tooltip,
} from '@mui/material'
import { Clock, Calendar, CalendarDays, CalendarRange, Code, Timer } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface CronBuilderProps {
  value: string // Cron expression in the selected schedule timezone
  onChange: (cronExpression: string) => void
  label?: string
  helperText?: string
}

type Frequency = 'minute' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom'
type Period = 'AM' | 'PM'

interface CronState {
  frequency: Frequency
  minuteInterval: number
  hourInterval: number
  startingMinute: number
  hour: number
  minute: number
  selectedDays: boolean[]
  dayOfMonth: number
  customCron: string
}

interface TimeInputProps {
  hour12: number
  minute: number
  ampm: Period
  amLabel: string
  pmLabel: string
  onTimeChange: (hour12: number, minute: number, ampm: Period) => void
}

const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 0]

const parseCron = (cronExpression: string): CronState => {
  const parts = cronExpression.trim().split(/\s+/)
  if (parts.length !== 5) {
    return {
      frequency: 'daily',
      minuteInterval: 5,
      hourInterval: 6,
      startingMinute: 0,
      hour: 2,
      minute: 0,
      selectedDays: [true, false, false, false, false, false, false],
      dayOfMonth: 1,
      customCron: cronExpression,
    }
  }

  const [minute, hour, day, , dayOfWeek] = parts

  if (minute.startsWith('*/') && hour === '*' && day === '*' && dayOfWeek === '*') {
    return {
      frequency: 'minute',
      minuteInterval: parseInt(minute.replace('*/', '')) || 5,
      hourInterval: 6,
      startingMinute: 0,
      hour: 2,
      minute: 0,
      selectedDays: [true, false, false, false, false, false, false],
      dayOfMonth: 1,
      customCron: cronExpression,
    }
  }

  if (/^\d+$/.test(minute) && hour.startsWith('*/') && day === '*' && dayOfWeek === '*') {
    return {
      frequency: 'hourly',
      minuteInterval: 5,
      hourInterval: parseInt(hour.replace('*/', '')) || 6,
      startingMinute: parseInt(minute),
      hour: 2,
      minute: 0,
      selectedDays: [true, false, false, false, false, false, false],
      dayOfMonth: 1,
      customCron: cronExpression,
    }
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && day === '*' && dayOfWeek === '*') {
    return {
      frequency: 'daily',
      minuteInterval: 5,
      hourInterval: 6,
      startingMinute: 0,
      hour: parseInt(hour),
      minute: parseInt(minute),
      selectedDays: [true, false, false, false, false, false, false],
      dayOfMonth: 1,
      customCron: cronExpression,
    }
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && day === '*' && /^[\d,]+$/.test(dayOfWeek)) {
    const selectedDayNums = dayOfWeek.split(',').map((d) => parseInt(d))
    const selectedDays = DAY_NUMBERS.map((dayNum) => selectedDayNums.includes(dayNum))

    return {
      frequency: 'weekly',
      minuteInterval: 5,
      hourInterval: 6,
      startingMinute: 0,
      hour: parseInt(hour),
      minute: parseInt(minute),
      selectedDays,
      dayOfMonth: 1,
      customCron: cronExpression,
    }
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && /^\d+$/.test(day) && dayOfWeek === '*') {
    return {
      frequency: 'monthly',
      minuteInterval: 5,
      hourInterval: 6,
      startingMinute: 0,
      hour: parseInt(hour),
      minute: parseInt(minute),
      selectedDays: [true, false, false, false, false, false, false],
      dayOfMonth: parseInt(day),
      customCron: cronExpression,
    }
  }

  return {
    frequency: 'custom',
    minuteInterval: 5,
    hourInterval: 6,
    startingMinute: 0,
    hour: 2,
    minute: 0,
    selectedDays: [true, false, false, false, false, false, false],
    dayOfMonth: 1,
    customCron: cronExpression,
  }
}

const buildCron = (state: CronState): string => {
  switch (state.frequency) {
    case 'minute':
      return `*/${state.minuteInterval} * * * *`
    case 'hourly':
      return `${state.startingMinute} */${state.hourInterval} * * *`
    case 'daily':
      return `${state.minute} ${state.hour} * * *`
    case 'weekly': {
      const selectedDayNums = state.selectedDays
        .map((selected, idx) => (selected ? DAY_NUMBERS[idx] : null))
        .filter((d) => d !== null)
      if (selectedDayNums.length === 0) return `${state.minute} ${state.hour} * * 1`
      return `${state.minute} ${state.hour} * * ${selectedDayNums.join(',')}`
    }
    case 'monthly':
      return `${state.minute} ${state.hour} ${state.dayOfMonth} * *`
    case 'custom':
      return state.customCron
    default:
      return '0 2 * * *'
  }
}

const generatePreview = (
  state: CronState,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  switch (state.frequency) {
    case 'minute':
      return t('cronBuilder.everyMinutes', { count: state.minuteInterval })
    case 'hourly':
      return t('cronBuilder.everyHours', { count: state.hourInterval })
    case 'daily': {
      const hour12 = state.hour === 0 ? 12 : state.hour > 12 ? state.hour - 12 : state.hour
      const ampm = state.hour >= 12 ? t('cronBuilder.pm') : t('cronBuilder.am')
      const minuteStr = state.minute.toString().padStart(2, '0')
      return t('cronBuilder.dailyAt', { time: `${hour12}:${minuteStr} ${ampm}` })
    }
    case 'weekly': {
      // Re-enable DAY_FULL mapping if needed or use shorter
      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      const selectedDayNames = dayNames.filter((_, idx) => state.selectedDays[idx])
      if (selectedDayNames.length === 0) return t('cronBuilder.noDaysSelected')
      const hour12 = state.hour === 0 ? 12 : state.hour > 12 ? state.hour - 12 : state.hour
      const ampm = state.hour >= 12 ? t('cronBuilder.pm') : t('cronBuilder.am')
      const minuteStr = state.minute.toString().padStart(2, '0')
      const daysStr =
        selectedDayNames.length === 7 ? t('cronBuilder.daily') : selectedDayNames.join(', ')
      return t('cronBuilder.weeklyAt', { days: daysStr, time: `${hour12}:${minuteStr} ${ampm}` })
    }
    case 'monthly': {
      const hour12 = state.hour === 0 ? 12 : state.hour > 12 ? state.hour - 12 : state.hour
      const ampm = state.hour >= 12 ? t('cronBuilder.pm') : t('cronBuilder.am')
      const minuteStr = state.minute.toString().padStart(2, '0')
      const suffix =
        state.dayOfMonth === 1
          ? 'st'
          : state.dayOfMonth === 2
            ? 'nd'
            : state.dayOfMonth === 3
              ? 'rd'
              : 'th'
      return t('cronBuilder.monthlyOn', {
        day: `${state.dayOfMonth}${suffix}`,
        time: `${hour12}:${minuteStr} ${ampm}`,
      })
    }
    case 'custom':
      return t('cronBuilder.customSchedule', { cron: state.customCron })
    default:
      return ''
  }
}

function TimeInput({ hour12, minute, ampm, amLabel, pmLabel, onTimeChange }: TimeInputProps) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: 'center',
      }}
    >
      <TextField
        type="number"
        value={hour12}
        onChange={(e) => {
          const newHour12 = Math.max(1, Math.min(12, parseInt(e.target.value) || 1))
          onTimeChange(newHour12, minute, ampm)
        }}
        variant="outlined"
        size="small"
        sx={{
          width: 50,
          '& .MuiInputBase-input': { p: '6px', textAlign: 'center', fontSize: '0.875rem' },
        }}
        slotProps={{
          htmlInput: { min: 1, max: 12 },
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        :
      </Typography>
      <TextField
        type="number"
        value={minute.toString().padStart(2, '0')}
        onChange={(e) => {
          const newMinute = Math.max(0, Math.min(59, parseInt(e.target.value) || 0))
          onTimeChange(hour12, newMinute, ampm)
        }}
        variant="outlined"
        size="small"
        sx={{
          width: 50,
          '& .MuiInputBase-input': { p: '6px', textAlign: 'center', fontSize: '0.875rem' },
        }}
        slotProps={{
          htmlInput: { min: 0, max: 59 },
        }}
      />
      <Select
        value={ampm}
        onChange={(e) => onTimeChange(hour12, minute, e.target.value as Period)}
        variant="outlined"
        size="small"
        sx={{
          width: 65,
          '& .MuiSelect-select': { p: '6px 24px 6px 8px !important', fontSize: '0.875rem' },
        }}
      >
        <MenuItem value="AM" sx={{ fontSize: '0.875rem' }}>
          {amLabel}
        </MenuItem>
        <MenuItem value="PM" sx={{ fontSize: '0.875rem' }}>
          {pmLabel}
        </MenuItem>
      </Select>
    </Stack>
  )
}

export default function CronBuilder({ value, onChange, label, helperText }: CronBuilderProps) {
  const { t } = useTranslation()
  const [state, setState] = useState<CronState>(parseCron(value))

  useEffect(() => {
    setState(parseCron(value))
  }, [value])

  const handleStateChange = (newState: Partial<CronState>) => {
    const updatedState = { ...state, ...newState }
    setState(updatedState)

    onChange(buildCron(updatedState))
  }

  const hour12 = state.hour === 0 ? 12 : state.hour > 12 ? state.hour - 12 : state.hour
  const ampm = state.hour >= 12 ? 'PM' : 'AM'

  const handleTimeChange = (hour12: number, minute: number, ampm: Period) => {
    let hour24 = hour12
    if (ampm === 'AM' && hour12 === 12) hour24 = 0
    else if (ampm === 'PM' && hour12 !== 12) hour24 = hour12 + 12
    handleStateChange({ hour: hour24, minute })
  }

  const handleFrequencyChange = (
    _: React.SyntheticEvent | null,
    newFrequency: Frequency | null
  ) => {
    if (newFrequency !== null) {
      handleStateChange({ frequency: newFrequency })
    }
  }

  const timeInput = (
    <TimeInput
      hour12={hour12}
      minute={state.minute}
      ampm={ampm}
      amLabel={t('cronBuilder.am')}
      pmLabel={t('cronBuilder.pm')}
      onTimeChange={handleTimeChange}
    />
  )

  return (
    <Stack spacing={1.5}>
      {label && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            ml: 1,
          }}
        >
          {label}
        </Typography>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 0,
          overflow: 'hidden',
          borderRadius: 2,
          borderColor: (theme) => alpha(theme.palette.divider, 0.6),
        }}
      >
        {/* Frequency Selector */}
        <Box
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default', p: 0.5 }}
        >
          <ToggleButtonGroup
            value={state.frequency}
            exclusive
            onChange={handleFrequencyChange}
            fullWidth
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: 0,
                borderRadius: 1,
                mx: 0.5,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'none',
                height: 28, // Fixed height for tabs
                '&.Mui-selected': {
                  bgcolor: 'background.paper',
                  color: 'primary.main',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                },
              },
            }}
          >
            <ToggleButton value="minute">
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Timer size={14} /> <span>{t('cronBuilder.tabs.minutes')}</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="hourly">
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Clock size={14} /> <span>{t('cronBuilder.tabs.hourly')}</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="daily">
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Calendar size={14} /> <span>{t('cronBuilder.tabs.daily')}</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="weekly">
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                <CalendarDays size={14} /> <span>{t('cronBuilder.tabs.weekly')}</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="monthly">
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                <CalendarRange size={14} /> <span>{t('cronBuilder.tabs.monthly')}</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="custom">
              <span>{t('cronBuilder.tabs.custom')}</span>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Configuration Area */}
        <Box
          sx={{
            p: 2,
            height: 85, // Fixed height to prevent shifting layout
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center', // Center content vertically and horizontally
          }}
        >
          {state.frequency === 'minute' && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography variant="body2">{t('cronBuilderComponent.runEvery')}</Typography>
              <TextField
                type="number"
                value={state.minuteInterval}
                onChange={(e) =>
                  handleStateChange({ minuteInterval: Math.max(1, parseInt(e.target.value) || 1) })
                }
                variant="outlined"
                size="small"
                sx={{ width: 60, '& input': { textAlign: 'center', p: '6px' } }}
                slotProps={{
                  htmlInput: { min: 1, max: 59 },
                }}
              />
              <Typography variant="body2">{t('cronBuilderComponent.minutesSuffix')}</Typography>
            </Stack>
          )}

          {state.frequency === 'hourly' && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2">{t('cronBuilderComponent.runEvery')}</Typography>
              <TextField
                type="number"
                value={state.hourInterval}
                onChange={(e) =>
                  handleStateChange({ hourInterval: Math.max(1, parseInt(e.target.value) || 1) })
                }
                variant="outlined"
                size="small"
                sx={{ width: 60, '& input': { textAlign: 'center', p: '6px' } }}
                slotProps={{
                  htmlInput: { min: 1, max: 23 },
                }}
              />
              <Typography variant="body2">{t('cronBuilderComponent.hoursAtMinute')}</Typography>
              <TextField
                type="number"
                value={state.startingMinute}
                onChange={(e) =>
                  handleStateChange({
                    startingMinute: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)),
                  })
                }
                variant="outlined"
                size="small"
                sx={{ width: 60, '& input': { textAlign: 'center', p: '6px' } }}
                slotProps={{
                  htmlInput: { min: 0, max: 59 },
                }}
              />
              <Typography variant="body2">{t('cronBuilderComponent.pastTheHour')}</Typography>
            </Stack>
          )}

          {state.frequency === 'daily' && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography variant="body2">{t('cronBuilderComponent.runDailyAt')}</Typography>
              {timeInput}
            </Stack>
          )}

          {state.frequency === 'weekly' && (
            <Stack
              spacing={1}
              sx={{
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">{t('cronBuilderComponent.runOn')}</Typography>

                {/* ... inside component ... */}

                <ToggleButtonGroup
                  value={state.selectedDays
                    .map((s, i) => (s ? i.toString() : null))
                    .filter(Boolean)}
                  onChange={(_, newValues: string[]) => {
                    const newIndices = newValues.map((v) => parseInt(v))
                    const newSelected = DAYS.map((_, i) => newIndices.includes(i))
                    handleStateChange({ selectedDays: newSelected })
                  }}
                  size="small"
                >
                  {DAYS.map((d, i) => (
                    <Tooltip key={i} title={DAY_NAMES[i]} arrow>
                      <ToggleButton
                        value={i.toString()}
                        sx={{
                          width: 24,
                          height: 24,
                          p: 0,
                          borderRadius: '50% !important',
                          border: 'none',
                          ml: 0.25,
                          fontSize: '0.7rem',
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                          },
                        }}
                      >
                        {d}
                      </ToggleButton>
                    </Tooltip>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">{t('cronBuilderComponent.at')}</Typography>
                {timeInput}
              </Stack>
            </Stack>
          )}

          {state.frequency === 'monthly' && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography variant="body2">{t('cronBuilderComponent.runOnDay')}</Typography>
              <Select
                value={state.dayOfMonth}
                onChange={(e) => handleStateChange({ dayOfMonth: e.target.value as number })}
                variant="outlined"
                size="small"
                sx={{
                  mx: 1,
                  width: 60,
                  '& .MuiSelect-select': {
                    p: '6px 24px 6px 8px !important',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                  },
                }}
                MenuProps={{ slotProps: { paper: { sx: { maxHeight: 300 } } } }}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <MenuItem key={d} value={d} sx={{ fontSize: '0.875rem' }}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="body2">{t('cronBuilderComponent.at')}</Typography>
              {timeInput}
            </Stack>
          )}

          {state.frequency === 'custom' && (
            <TextField
              value={state.customCron}
              onChange={(e) => handleStateChange({ customCron: e.target.value })}
              fullWidth
              placeholder="* * * * *"
              variant="outlined"
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Code size={16} />
                    </InputAdornment>
                  ),
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                },
              }}
            />
          )}
        </Box>

        {/* Preview Footer */}
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
            >
              {generatePreview(state, t)}
            </Typography>
          </Stack>
          <Box
            component="span"
            sx={{
              fontFamily: 'monospace',
              bgcolor: 'background.paper',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              fontSize: '0.7rem',
            }}
          >
            {buildCron(state)}
          </Box>
        </Box>
      </Paper>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            ml: 1,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  )
}
